import React, { FC, useContext, useEffect, useMemo } from 'react';

import { useFormikContext } from 'formik';

// Components
import AampeStepperComponent from '../../../../../components/shared/AampeStepper/AampeStepperComponent';
// Constants
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';

import { EditorContext } from 'context/EditorContext';

// Hooks
import useMessageGenerationQueryV1 from '../../../../../hooks/formulas/useMessageGenerationQueryV1';
import useMessageContentCountQuery from 'hooks/message-content/useMessageContentCountQuery';

// Models
import {
    FormulaPreviewCount,
    FormulaTestMessageFormValues,
} from '../../../../../models/formula.model';
import { AampeStepperStepProps } from '../../../../../models/stepper.model';

import MessagePreviewMain from '../../../../../views/formulaCreate/messagePreview/MessagePreviewMain';

import TestFormulaFormMainV1 from '../../sections/TestFormulaFormMainV1';
import TestFormulaElementSelector from './TestFormulaElementSelector';

interface TestFormulaContainerProps {
    handleNeedHelp: () => void;
    isLoading: boolean;
    menuPortalId?: string;
    handleClose?: () => void;
    isModalView?: boolean;
    currentTab?: string;
}

const TestFormulaContainer: FC<TestFormulaContainerProps> = ({
    handleNeedHelp,
    isLoading,
    menuPortalId,
    handleClose,
    isModalView = false,
    currentTab,
}) => {
    const { values, setFieldValue } =
        useFormikContext<FormulaTestMessageFormValues>();
    const { editorDetails, setEditorDetails } = useContext(EditorContext);

    const {
        data: alternateMessages,
        error,
        isPending,
    } = useMessageGenerationQueryV1(
        Number(editorDetails?.formula?.id),
        values?.message_content_id,
        FormulaPreviewCount.default,
        values?.filters,
    );

    const { data: alternateMessagesCount, isPending: messageCountPending } =
        useMessageContentCountQuery(
            values?.message_content_id,
            values.formula_id!,
            values?.filters,
        );

    useEffect(() => {
        setFieldValue('isNextButtonDisabled', alternateMessages?.length !== 1);
        setEditorDetails((prev) => ({
            ...prev,
            testPreviews: alternateMessages ?? [],
            previewError: error?.message || null,
            isPreviewError: !!error,
            isGenerating: isPending,
        }));
    }, [alternateMessages, error, isPending]);

    useEffect(() => {
        setEditorDetails((prev) => ({
            ...prev,
            alternateMessagesCount: alternateMessagesCount,
        }));
    }, [alternateMessagesCount, messageCountPending]);

    const steps = useMemo<AampeStepperStepProps[]>(
        () => [
            {
                title: 'Preview message',
                tabKey: 'preview',
                renderComponent: <TestFormulaElementSelector />,
                getCompletionStatus: () => alternateMessages?.length === 1,
                hideActionButtons: true,
                hideHeader: true,
            },
            {
                title: 'Select test audience',
                tabKey: 'audience',
                renderComponent: (
                    <TestFormulaFormMainV1
                        isLoading={isLoading}
                        handleNeedHelp={handleNeedHelp}
                        menuPortalId={menuPortalId}
                        handleClose={handleClose}
                        isModalView={isModalView}
                    />
                ),
                getCompletionStatus: () => false,
                hideActionButtons: true,
                hideHeader: true,
            },
        ],
        [
            isLoading,
            handleNeedHelp,
            menuPortalId,
            handleClose,
            isModalView,
            alternateMessages,
        ],
    );

    return (
        <Container>
            <StyledAampeStepperComponent
                steps={steps}
                currentTab={currentTab}
                showStepsAsCompleted
                containerStyle={{
                    padding: 0,
                }}
                stepsStyle={{
                    padding: 0,
                }}
            />
            <StyledMessagePreviewMain showTestPreview />
        </Container>
    );
};
export default TestFormulaContainer;

const Container = styled.div({
    display: 'flex',
    width: '100%',
});

const StyledAampeStepperComponent = styled(AampeStepperComponent)({
    display: 'flex',
    gap: 'auto',
    width: '50%',
    padding: aampeTheme.spacing4(6),
    borderRight: `1px solid ${aampeTheme.colors.interactiveWG01}`,
});

const StyledMessagePreviewMain = styled(MessagePreviewMain)({
    display: 'flex',
    gap: 'auto',
    width: '50%',
    padding: aampeTheme.spacing4(6),
});
